import { useEffect, useState } from 'react';
import { Form, Select, InputNumber, DatePicker, Input, Button } from 'antd';
import dayjs from 'dayjs';
import { FluxoCaixaModalShell } from './FluxoCaixaModalShell';
import {
  FluxoCaixaCentroCusto,
  FluxoCaixaFormData,
  FluxoCaixaLancamento,
  FluxoCaixaTipo,
  FLUXO_CAIXA_CENTRO_CUSTO_LABELS,
  FLUXO_CAIXA_CATEGORIA_LABELS,
  getCategoriasPorTipo,
} from '../types/fluxoCaixa';

interface FluxoCaixaEditModalProps {
  visible: boolean;
  lancamento?: FluxoCaixaLancamento;
  onSave: (id: number, data: Partial<FluxoCaixaFormData>) => Promise<void>;
  onCancel: () => void;
}

export function FluxoCaixaEditModal({
  visible,
  lancamento,
  onSave,
  onCancel,
}: FluxoCaixaEditModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && lancamento) {
      form.setFieldsValue({
        categoria: lancamento.categoria,
        centro_custo: lancamento.centro_custo,
        valor: lancamento.valor,
        descricao: lancamento.descricao,
        documento: lancamento.documento,
        data_lancamento: dayjs(lancamento.data_lancamento),
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, lancamento, form]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!lancamento) return;

    try {
      setSubmitting(true);
      const formData: Partial<FluxoCaixaFormData> = {
        categoria: values.categoria as FluxoCaixaFormData['categoria'],
        centro_custo: values.centro_custo as FluxoCaixaCentroCusto | undefined,
        valor: values.valor as number,
        descricao: values.descricao as string | undefined,
        documento: values.documento as string | undefined,
        data_lancamento: (values.data_lancamento as dayjs.Dayjs).format('YYYY-MM-DD'),
      };

      await onSave(lancamento.id, formData);
      onCancel();
    } finally {
      setSubmitting(false);
    }
  };

  const categorias = lancamento
    ? getCategoriasPorTipo(lancamento.lancamento_tipo)
    : [];

  const tipoLabel =
    lancamento?.lancamento_tipo === FluxoCaixaTipo.ENTRADA ? 'Entrada' : 'Saída';

  return (
    <FluxoCaixaModalShell
      open={visible}
      onCancel={onCancel}
      eyebrow="Editar Lançamento"
      title="Atualize os dados do lançamento"
      width={520}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        {lancamento && (
          <Form.Item label="Tipo">
            <div className="px-3 py-2 bg-[#f5f6f8] dark:bg-zinc-800 rounded-lg text-sm font-medium text-gray-900 dark:text-white">
              {tipoLabel}
            </div>
          </Form.Item>
        )}

        <Form.Item
          name="categoria"
          label="Categoria"
          rules={[{ required: true, message: 'Selecione a categoria' }]}
        >
          <Select>
            {categorias.map(cat => (
              <Select.Option key={cat} value={cat}>
                {FLUXO_CAIXA_CATEGORIA_LABELS[cat]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="valor"
            label="Valor (R$)"
            rules={[
              { required: true, message: 'Informe o valor' },
              { type: 'number', min: 0.01, message: 'Valor deve ser maior que zero' },
            ]}
          >
            <InputNumber
              min={0.01}
              step={0.01}
              precision={2}
              decimalSeparator=","
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="data_lancamento"
            label="Data"
            rules={[{ required: true, message: 'Informe a data' }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              disabledDate={current => current && current > dayjs().endOf('day')}
            />
          </Form.Item>
        </div>

        <Form.Item name="centro_custo" label="Centro de Custo">
          <Select allowClear placeholder="Opcional">
            {Object.values(FluxoCaixaCentroCusto).map(cc => (
              <Select.Option key={cc} value={cc}>
                {FLUXO_CAIXA_CENTRO_CUSTO_LABELS[cc]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="documento" label="Documento / Referência">
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item name="descricao" label="Descrição">
          <Input.TextArea maxLength={500} rows={3} showCount />
        </Form.Item>

        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-zinc-700">
          <Button onClick={onCancel} style={{ flex: 1 }} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="primary"
            loading={submitting}
            onClick={() => form.submit()}
            style={{ flex: 1, backgroundColor: '#FF3737' }}
          >
            {submitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </Form>
    </FluxoCaixaModalShell>
  );
}
